import { PropsWithChildren, FC, useState } from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { ButtonVariants } from 'constants/common';
import { DropdownMenuItems } from 'interfaces/shared.interface';
import { Button } from 'shared-components/index';
import { cn } from 'utils/global';

interface Props {
  elements: DropdownMenuItems[];
  onItemClick?: (element: DropdownMenuItems) => void;
  dataCy?: string;
  buttonVariant?: ButtonVariants;
  hidden?: boolean;
  disabled?: boolean;
  icon?: IconProp;
  className?: string;
  visibleDropdownClassName?: string;
  dropdownMenuClassName?: string;
}

const DropdownMenu: FC<PropsWithChildren<Props>> = ({
  elements,
  hidden,
  buttonVariant,
  dataCy,
  onItemClick,
  children,
  disabled,
  icon,
  className,
  visibleDropdownClassName,
  dropdownMenuClassName,
}) => {
  const { t } = useTranslation();

  const [isVisibleDropdown, setIsVisibleDropdown] = useState(false);

  const hasVisibleMenuItems = elements.some((item) => !item.hidden);

  const toggleMenuVisibility = () => {
    setIsVisibleDropdown(!isVisibleDropdown);
  };

  if (!hasVisibleMenuItems || hidden) {
    return null;
  }

  return (
    <>
      {isVisibleDropdown && <div onClick={toggleMenuVisibility} className="fixed w-full h-full left-0 top-0 z-10" />}

      <div
        data-cy={dataCy}
        className={cn(className, 'relative flex justify-center', {
          'cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        })}
      >
        <Button
          leftIcon={icon}
          dataCy="dropdown-menu-button"
          variant={buttonVariant || ButtonVariants.PLAIN}
          type="button"
          onClick={() => !disabled && setIsVisibleDropdown(!isVisibleDropdown)}
          className={cn('dark:active:!bg-neutral-800', dropdownMenuClassName, {
            'cursor-not-allowed': disabled,
          })}
        >
          {children || (
            <FontAwesomeIcon
              icon={faEllipsisVertical}
              className={cn('text-neutral-700 dark:text-neutral-300', {
                'text-neutral-300 dark:text-neutral-700': disabled,
              })}
            />
          )}
        </Button>

        {isVisibleDropdown && (
          <div
            data-cy="dropdown-menu"
            className={cn(
              visibleDropdownClassName,
              'origin-top-right border border-neutral-200 absolute right-0 mt-2 py-2 w-56 rounded-md z-20 bg-white-00 dark:bg-black-900 dark:border-neutral-800  dark:border items-center'
            )}
          >
            {elements
              .filter(({ hidden }) => !hidden)
              .map((element) => (
                <button
                  data-cy={element.dataCy}
                  key={element.name}
                  onClick={() => {
                    if (!element.disabled) {
                      onItemClick?.(element);
                      element.actionOnClick();
                      toggleMenuVisibility();
                    }
                  }}
                  disabled={element.disabled}
                  className={cn(
                    element.className,
                    '!text-neutral-700 dark:text-white-00 block pl-4 font-medium text-bodyMd w-full text-left hover:bg-neutral-50 dark:hover:bg-neutral-900',
                    {
                      'cursor-not-allowed opacity-50 hover:bg-inherit! dark:hover:bg-inherit!': element.disabled,
                      flex: element.icon,
                    }
                  )}
                >
                  {element.icon && (
                    <FontAwesomeIcon icon={element.icon} className={cn(element.iconClassName, 'flex')} />
                  )}
                  <p className="text-neutral-800 dark:text-neutral-200">{t(element.name)}</p>
                </button>
              ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DropdownMenu;
